import { useQuery } from '@tanstack/react-query';

import { policies, type Policy } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';

import { BilletsDialog } from '../../portfolio/BilletsDialog';
import {
  chaveDaApoliceDaCotacao,
  MOTIVO_COTACAO_EMITIDA,
} from '../../portfolio/boletoAnexado';

import { QuoteBilletsDialog } from './QuoteBilletsDialog';

/**
 * "Baixar boleto(s)" da corretora, na linha do tempo e no menu da lista (R5-A08).
 *
 * Antes da emissao, os boletos sao os das parcelas da cotacao. Depois da emissao,
 * parcelas e boletos sao os da apolice: a apolice copia o boleto e pode troca-lo, e o
 * da cotacao fica congelado. Por isso a cotacao emitida abre o dialogo da apolice,
 * achada pela lista de apolices da corretora (filtro pela operacao, com o escopo). A
 * cotacao recusada ou cancelada abre o dialogo da cotacao com o download desligado e
 * o motivo.
 */
export function BoletosDaCotacao({
  operationId,
  numero,
  emitida,
  apolice,
  open,
  onClose,
}: {
  operationId: string;
  numero: string;
  emitida: boolean;
  /** A apolice ja lida pela tela, quando ha. Sem ela, o dialogo procura. */
  apolice?: Policy | undefined;
  open: boolean;
  onClose: () => void;
}) {
  const procurada = useQuery({
    queryKey: chaveDaApoliceDaCotacao(operationId),
    queryFn: ({ signal }) =>
      policies.list({ operation_id: operationId, limit: 1 }, signal),
    enabled: open && emitida && apolice === undefined,
    retry: false,
  });

  if (!emitida) {
    return (
      <QuoteBilletsDialog
        operationId={operationId}
        numero={numero}
        open={open}
        onClose={onClose}
      />
    );
  }

  const daCotacao = apolice ?? procurada.data?.items[0];
  if (daCotacao || procurada.isPending) {
    // Enquanto a apolice e lida, o dialogo da apolice mostra o carregamento.
    return <BilletsDialog policy={daCotacao ?? null} open={open} onClose={onClose} />;
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Boletos"
      description={`Cotacao ${numero}`}
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      {procurada.isError ? (
        <Alert tone="danger" title="Nao foi possivel encontrar a apolice">
          {procurada.error.message}
        </Alert>
      ) : (
        <p className="text-sm text-ink-600">
          {MOTIVO_COTACAO_EMITIDA} A apolice desta cotacao ainda nao aparece na lista de
          apolices.
        </p>
      )}
    </Modal>
  );
}
